import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BiReset, BiSearch, BiSolidGraduation } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { ADMISSIONS_WRITE_ROLES, hasRole } from '@/auth/roles';
import { getBranches, getGrades, getSchools } from '@/api/lookups.api';
import { listStudents, restoreStudent } from '@/api/students.api';
import { ApiError } from '@/api/errors';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { Banner } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { showSuccessToast } from '@/lib/toast';
import { formatDateTime } from '@/lib/format';
import { lookupLabel } from '@/lib/lookups';
import { hasMultipleBranches } from '@/lib/branchScope';
import type { Gender, LookupItem, PaginatedStudents, StudentResponse } from '@/types/shared';
import './StudentsListPage.css';

export function StudentsListPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canRegister = hasRole(user?.role, ADMISSIONS_WRITE_ROLES);
  const isAdmin = user?.role === 'Admin';
  const showBranchColumn = hasMultipleBranches(user);
  const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState<number | ''>('');
  const [schoolFilter, setSchoolFilter] = useState<number | ''>('');
  const [genderFilter, setGenderFilter] = useState<Gender | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [branches, setBranches] = useState<LookupItem[]>([]);

  const [data, setData] = useState<PaginatedStudents | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [restoreCode, setRestoreCode] = useState('');
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState<{
    tone: 'success' | 'danger';
    text: string;
  } | null>(null);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter/search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search, gradeFilter, schoolFilter, genderFilter, branchFilter, includeDeleted]);

  useEffect(() => {
    getGrades()
      .then(setGrades)
      .catch(() => setGrades([]));
    getSchools()
      .then(setSchools)
      .catch(() => setSchools([]));
  }, []);

  // T175 (§20, `docs/design/19-branch-scoping-ux.md` §5.2) — only fetched at all when the branch
  // filter can actually render; a single-branch user never sees this network call.
  useEffect(() => {
    if (!showBranchColumn) return;
    getBranches()
      .then(setBranches)
      .catch(() => setBranches([]));
  }, [showBranchColumn]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listStudents({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
      grade: gradeFilter === '' ? undefined : gradeFilter,
      agreedSchool: schoolFilter === '' ? undefined : schoolFilter,
      gender: genderFilter === '' ? undefined : genderFilter,
      branch: branchFilter === '' ? undefined : branchFilter,
      includeDeleted: isAdmin && includeDeleted ? true : undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('students.error.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    sort,
    search,
    gradeFilter,
    schoolFilter,
    genderFilter,
    branchFilter,
    includeDeleted,
    isAdmin,
    t,
    reloadToken,
  ]);

  const activeFilterCount = [
    Boolean(search),
    gradeFilter !== '',
    schoolFilter !== '',
    genderFilter !== '',
    branchFilter !== '',
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setGradeFilter('');
    setSchoolFilter('');
    setGenderFilter('');
    setBranchFilter('');
  }

  // 14-tables-forms-restyle-spec.md §3.2/§3.3 — `includeDeleted` intentionally excluded (Admin-only
  // scope toggle, not a narrowing filter in the same sense).
  const activeFilterChips: ActiveFilterChip[] = [
    branchFilter !== '' && {
      key: 'branch',
      label: t('students.filters.branch'),
      valueLabel: lookupLabel(
        branches.find((branch) => branch.id === branchFilter),
        i18n.language,
      ),
      onRemove: () => setBranchFilter(''),
    },
    gradeFilter !== '' && {
      key: 'grade',
      label: t('students.filters.grade'),
      valueLabel: lookupLabel(
        grades.find((grade) => grade.id === gradeFilter),
        i18n.language,
      ),
      onRemove: () => setGradeFilter(''),
    },
    schoolFilter !== '' && {
      key: 'school',
      label: t('students.filters.school'),
      valueLabel: lookupLabel(
        schools.find((school) => school.id === schoolFilter),
        i18n.language,
      ),
      onRemove: () => setSchoolFilter(''),
    },
    genderFilter !== '' && {
      key: 'gender',
      label: t('students.filters.gender'),
      valueLabel: t(`enums.gender.${genderFilter}`),
      onRemove: () => setGenderFilter(''),
    },
  ].filter(Boolean) as ActiveFilterChip[];

  async function handleRestoreSubmit(e: FormEvent) {
    e.preventDefault();
    const code = restoreCode.trim();
    if (!code) return;
    setRestoreSubmitting(true);
    setRestoreMessage(null);
    try {
      await restoreStudent(code);
      const message = t('students.restore.success', { code });
      setRestoreMessage({ tone: 'success', text: message });
      showSuccessToast(message);
      setRestoreCode('');
      setReloadToken((v) => v + 1);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const text =
        apiError.code === 'NOT_FOUND'
          ? t('students.restore.notFound')
          : t('students.restore.genericError');
      setRestoreMessage({ tone: 'danger', text });
    } finally {
      setRestoreSubmitting(false);
    }
  }

  const columns: DataTableColumn<StudentResponse>[] = [
    {
      key: 'studentCode',
      header: t('students.columns.studentCode'),
      sortField: 'studentCode',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => (
        <span className="fa-students-list__code-cell">
          <Link to={`/students/${row.studentCode}`} className="fa-code-pill fa-ltr-isolate">
            {row.studentCode}
          </Link>
          {row.deletedAt && <StatusChip label={t('students.deletedBadge')} tone="danger" />}
        </span>
      ),
    },
    ...(showBranchColumn
      ? [
          {
            key: 'branch',
            header: t('students.columns.branch'),
            toggleable: true,
            render: (row: StudentResponse) => <bdi>{lookupLabel(row.branch, i18n.language)}</bdi>,
          } satisfies DataTableColumn<StudentResponse>,
        ]
      : []),
    {
      key: 'nameAr',
      header: t('students.columns.nameAr'),
      sortField: 'nameAr',
      render: (row) => row.nameAr,
    },
    {
      key: 'nameEn',
      header: t('students.columns.nameEn'),
      sortField: 'nameEn',
      render: (row) => row.nameEn,
    },
    {
      key: 'grade',
      header: t('students.columns.grade'),
      render: (row) => lookupLabel(row.grade, i18n.language),
    },
    {
      key: 'school',
      header: t('students.columns.school'),
      render: (row) => lookupLabel(row.agreedSchool, i18n.language),
    },
    {
      key: 'gender',
      header: t('students.columns.gender'),
      render: (row) => <StatusChip label={t(`enums.gender.${row.gender}`)} tone="neutral" />,
    },
    {
      key: 'audit',
      header: t('students.columns.audit'),
      sortField: 'createdAt',
      render: (row) => (
        <span className="fa-students-list__audit">
          <span className="fa-students-list__audit-name">
            {row.createdBy?.fullName ?? t('students.detail.unknownUser')}
          </span>
          <bdi className="fa-students-list__audit-date">
            {formatDateTime(row.createdAt, i18n.language)}
          </bdi>
        </span>
      ),
    },
  ];

  return (
    <section className="fa-students-list">
      {successMessage && <Banner tone="success">{successMessage}</Banner>}
      <div className="fa-students-list__header">
        <div>
          <h1 className="fa-students-list__title">{t('students.listTitle')}</h1>
          <p className="fa-students-list__subtitle">
            {data ? t('students.resultCount', { count: data.meta.total }) : ' '}
          </p>
        </div>
        {canRegister && (
          <button
            type="button"
            className="fa-btn fa-btn--primary"
            onClick={() => navigate('/students/new')}
          >
            {t('students.registerButton')}
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.studentCode}
        caption={t('students.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((v) => v + 1)}
        onRowClick={(row) => navigate(`/students/${row.studentCode}`)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('students.searchPlaceholder'),
          ariaLabel: t('students.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        exportConfig={{
          endpoint: '/students/export',
          // Mirrors the list call's active filters — minus page/pageSize (the export endpoint
          // always streams every matching row, never one page) and `includeDeleted` (`
          // ExportStudentsQuery` has no such field — export is always the active-only set).
          params: {
            sort: sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined,
            search: search || undefined,
            grade: gradeFilter === '' ? undefined : gradeFilter,
            agreedSchool: schoolFilter === '' ? undefined : schoolFilter,
            gender: genderFilter === '' ? undefined : genderFilter,
            branch: branchFilter === '' ? undefined : branchFilter,
          },
          fallbackFilename: 'Students_export.xlsx',
        }}
        columnVisibility={{
          storageKey: 'students-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersDrawerTitle={t('students.filters.groupLabel')}
        filtersSlot={
          <div className="fa-students-list__filters-group">
            {showBranchColumn && (
              <label className="fa-students-list__filter">
                <span className="fa-students-list__filter-label">
                  {t('students.filters.branch')}
                </span>
                <select
                  value={branchFilter}
                  onChange={(e) =>
                    setBranchFilter(e.target.value === '' ? '' : Number(e.target.value))
                  }
                >
                  <option value="">{t('students.filters.allBranches')}</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {lookupLabel(branch, i18n.language)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="fa-students-list__filter">
              <span className="fa-students-list__filter-label">{t('students.filters.grade')}</span>
              <select
                value={gradeFilter}
                onChange={(e) =>
                  setGradeFilter(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                <option value="">{t('students.filters.allGrades')}</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {lookupLabel(grade, i18n.language)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fa-students-list__filter">
              <span className="fa-students-list__filter-label">{t('students.filters.school')}</span>
              <select
                value={schoolFilter}
                onChange={(e) =>
                  setSchoolFilter(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                <option value="">{t('students.filters.allSchools')}</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {lookupLabel(school, i18n.language)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fa-students-list__filter">
              <span className="fa-students-list__filter-label">{t('students.filters.gender')}</span>
              <select
                value={genderFilter}
                onChange={(e) =>
                  setGenderFilter(e.target.value === '' ? '' : (e.target.value as Gender))
                }
              >
                <option value="">{t('students.filters.allGenders')}</option>
                <option value="Male">{t('enums.gender.Male')}</option>
                <option value="Female">{t('enums.gender.Female')}</option>
              </select>
            </label>
            {isAdmin && (
              <label className="fa-students-list__filter fa-students-list__filter--checkbox">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                {t('students.filters.includeDeleted')}
              </label>
            )}
          </div>
        }
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              icon={BiSearch}
              title={t('students.empty.noMatchesTitle')}
              hint={t('students.empty.noMatchesHint')}
              action={
                <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
                  {t('students.filters.clear')}
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={BiSolidGraduation}
              title={t('students.empty.noStudentsTitle')}
              hint={t('students.empty.noStudentsHint')}
              action={
                canRegister && (
                  <button
                    type="button"
                    className="fa-btn fa-btn--primary"
                    onClick={() => navigate('/students/new')}
                  >
                    {t('students.empty.registerFirst')}
                  </button>
                )
              }
            />
          )
        }
      />

      {isAdmin && (
        <div className="fa-students-list__restore">
          <div className="fa-students-list__restore-heading">
            <span className="fa-students-list__restore-icon" aria-hidden="true">
              <BiReset />
            </span>
            <div>
              <h2 className="fa-students-list__restore-title">
                {t('students.restore.sectionTitle')}
              </h2>
              <p className="fa-students-list__restore-hint">{t('students.restore.hint')}</p>
            </div>
          </div>
          {restoreMessage && (
            <Banner tone={restoreMessage.tone} assertive={restoreMessage.tone === 'danger'}>
              {restoreMessage.text}
            </Banner>
          )}
          <form className="fa-students-list__restore-form" onSubmit={handleRestoreSubmit}>
            <label>
              <span className="fa-visually-hidden">{t('students.restore.codeAriaLabel')}</span>
              <input
                type="text"
                className="fa-ltr-isolate"
                value={restoreCode}
                placeholder={t('students.restore.codePlaceholder')}
                disabled={restoreSubmitting}
                onChange={(e) => setRestoreCode(e.target.value)}
              />
            </label>
            <button type="submit" className="fa-btn fa-btn--secondary" disabled={restoreSubmitting}>
              {t('students.restore.submit')}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
